"use client";

import { useEffect, useState } from "react";

type ConnectorProps = {
  containerId: string;
  strokeWidth?: number;
};

type ConnectorState = {
  path: string;
  width: number;
  height: number;
};

type Point = {
  x: number;
  y: number;
};

type NodeGeometry = {
  center: Point;
  top: number;
  bottom: number;
};

export default function TimelineConnectors({
  containerId,
  strokeWidth = 2,
}: ConnectorProps) {
  const [state, setState] = useState<ConnectorState | null>(null);

  useEffect(() => {
    const container = document.getElementById(containerId);
    if (!container) {
      return;
    }

    const threshold = 12; // px difference to consider items on the same row

    const update = () => {
      const nodes = Array.from(
        container.querySelectorAll<HTMLElement>("[data-timeline-node]")
      );

      if (nodes.length < 2) {
        setState(null);
        return;
      }

      const containerRect = container.getBoundingClientRect();

      const geometries: NodeGeometry[] = nodes.map((node) => {
        const rect = node.getBoundingClientRect();
        return {
          center: {
            x: rect.left - containerRect.left + rect.width / 2,
            y: rect.top - containerRect.top + rect.height / 2,
          },
          top: rect.top - containerRect.top,
          bottom: rect.bottom - containerRect.top,
        };
      });

      const first = geometries[0];
      if (!first) {
        setState(null);
        return;
      }

      const segments: Point[] = [first.center];

      for (let index = 1; index < geometries.length; index += 1) {
        const previous = geometries[index - 1];
        const current = geometries[index];
        if (!previous || !current) {
          continue;
        }
        const deltaY = current.center.y - previous.center.y;

        if (Math.abs(deltaY) <= threshold) {
          // Same row: go horizontally, then down if needed
          segments.push({ x: current.center.x, y: previous.center.y });
          segments.push(current.center);
        } else {
          // Different row: create right-angle turn
          const prevBottom = previous.bottom;
          const currTop = current.top;
          const midY = prevBottom + (currTop - prevBottom) / 2;

          segments.push({ x: previous.center.x, y: midY });
          segments.push({ x: current.center.x, y: midY });
          segments.push(current.center);
        }
      }

      const path = segments
        .map((point, idx) => `${idx === 0 ? "M" : "L"}${point.x},${point.y}`)
        .join(" ");

      setState({
        path,
        width: containerRect.width,
        height: containerRect.height,
      });
    };

    update();

    const resizeObserver = new ResizeObserver(() => update());
    resizeObserver.observe(container);
    const nodes = Array.from(
      container.querySelectorAll<HTMLElement>("[data-timeline-node]")
    );
    nodes.forEach((node) => {
      resizeObserver.observe(node);
    });

    window.addEventListener("resize", update);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [containerId]);

  if (!state || !state.path) {
    return null;
  }

  return (
    <svg
      className="pointer-events-none absolute inset-0 text-primary"
      viewBox={`0 0 ${state.width} ${state.height}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <path
        d={state.path}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
        strokeLinecap="round"
      />
    </svg>
  );
}
